const _ = require("lodash");
const StateReset = require("../../core/vault/StateReset");
const OpsParentAcc = require("./OpsParentAcc");
const OpsParentPayments = require("./OpsParentPayments");
const OpsParentSubChange = require("./OpsParentSubChange");
const AT = require("../../core/vault/ActionTypes");
const Slots = require("../../core/vault/Slots");
const {
	atUser,
	atPage,
	atParentAccount,
	atParentStudents,
	atParentPayments,
	atOfficeStudentData,
	atUserStudents,
	atUserLocks,
} = require("../../core/vault/StateCursors");
const LoadState = require("../../core/vault/util/LoadState");
const OpsLockOverrides = require("../../core/vault/OpsLockOverrides");
const OpsUser = require("../../core/vault/OpsUser");
const {setIn, extendIn, getIn} = require("../../../shared/ImmHelpers");
const URLMake = require("../../../shared/URLMake");
const UpdateReport = require("../../report/vault/UpdateReport");
const {track} = require("../../core/util/Mixpanel");
const {
	STUDENT_REGISTRATION_EVENTS,
} = require("../../../shared/events/registration");
const {getNextPrimarySetupStep} = require("../util/PrimaryRegistrationUtil");
const {goToPrimarySetupStep} = require("./goToPrimarySetupStep");

// Used as a reset hook.
const doParentStateReset = function(state, initState, now) {
	state = extendIn(state, [], {
		parentAccount: OpsParentAcc.init(now),
	});
	return state;
};

const fixSubChangePageStep = function(state) {
	const page = atPage(state).get();
	if (page.name === "parent.subscribe") {
		const parentAcc = atParentAccount(state).get();
		const steps = OpsParentSubChange.getAvailableSteps(parentAcc);
		// The back button can take us to a confirm page. This check makes sure
		// that the page only gets shown if it won't error.
		if (parentAcc.lastFinishedSCS) {
			steps.push("confirm");
		}
		const stepInd = steps.indexOf(page.step);
		if (stepInd === -1 && page.step === "confirm") {
			state = atPage(state).set(URLMake.state.forAccount());
		} else if (stepInd === -1) {
			state = atPage(state).setIn("step", steps[0]);
		}
	}
	return state;
};

//Helper functions used for various updates to the user
const updateUserStudentsList = function(state, userID, updates) {
	return atUser(state, userID).setIn(["model", "students"], ss => {
		return _.map(ss, s => (s.id === userID ? _.extend({}, s, updates) : s));
	});
};

const actionHandlers = {
	// Note that this may run on a state object that already ran this action on
	// the server, so avoid resetting what's already there.
	[AT.PARENT_INITIAL](vault, state, action) {
		const now = vault.clock.now();
		state = doParentStateReset(state, state, now);
		StateReset.addResetHook(vault, doParentStateReset);
		// If we're starting in the checkout URL with a bad step, fix it.
		state = fixSubChangePageStep(state);
		return state;
	},

	[AT.START_CHANGE_EMAIL](vault, state, action) {
		vault.emitEvent("beginChangeEmail", action.opts);
		return state;
	},

	[AT.LOAD_PARENT_ACCOUNT_REQUEST](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			return OpsParentAcc.loadStart(parentAcc, vault.api);
		});
		return state;
	},

	[AT.LOAD_PARENT_ACCOUNT_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.loadSuccess(
				parentAcc,
				action.data,
				vault.clock.now()
			);
			return parentAcc;
		});

		return state;
	},

	[AT.LOAD_BOOK_DEFERRALS_REQUEST](vault, state, action) {
		vault.api.loadBookDeferrals();
		return state;
	},

	[AT.LOAD_BOOK_DEFERRALS_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			return OpsParentAcc.deferralsLoaded(parentAcc, action.data.deferrals);
		});
		return state;
	},

	[AT.LOAD_BOOK_DEFERRALS_FAIL](vault, state, action) {
		// Non-fatal: the account page still works without deferral data.
		state = atParentAccount(state).set(parentAcc => {
			return OpsParentAcc.deferralsLoaded(parentAcc, []);
		});
		return state;
	},

	[AT.LOAD_DEFERRAL_PLACEMENT_REQUEST](vault, state, action) {
		vault.api.loadDeferralPlacement(action.platformUserID, action.studentID);
		return state;
	},

	[AT.LOAD_DEFERRAL_PLACEMENT_SUCCESS](vault, state, action) {
		vault.emitEvent("deferralPlacementLoaded", action.studentID, action.data);
		return state;
	},

	[AT.LOAD_DEFERRAL_PLACEMENT_FAIL](vault, state, action) {
		// Non-fatal: deferral dropdown keeps its provisional default.
		vault.emitEvent("deferralPlacementLoadFailed", action.studentID);
		return state;
	},

	[AT.RESOLVE_BOOK_DEFERRAL_REQUEST](vault, state, action) {
		vault.api.resolveBookDeferral(action.deferralID, action.bundleID);
		return state;
	},

	[AT.RESOLVE_BOOK_DEFERRAL_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			return OpsParentAcc.deferralResolved(
				parentAcc,
				action.payload.deferralID
			);
		});
		vault.emitEvent("bookDeferralResolved", action.data);
		return state;
	},

	[AT.RESOLVE_BOOK_DEFERRAL_FAIL](vault, state, action) {
		vault.emitEvent("bookDeferralResolveFail", action.deferralID);
		return state;
	},

	[AT.LOAD_PARENT_ACCOUNT_FAIL](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			return OpsParentAcc.loadError(parentAcc, action.error);
		});
		return state;
	},

	[AT.SAVE_PARENT_FORM_REQUEST](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			if (action.forSubChange) {
				parentAcc = OpsParentSubChange.setShippingSavingState(
					parentAcc,
					vault.emitEvent,
					"pending"
				);
			}
			parentAcc = OpsParentAcc.editFormData(
				parentAcc,
				vault.emitEvent,
				vault.api,
				action.section,
				action.vals
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SAVE_PARENT_FORM_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.saveFormSuccess(
				parentAcc,
				vault.emitEvent,
				action.data,
				action.payload,
				vault.clock.now()
			);
			parentAcc = OpsParentSubChange.setShippingSavingState(
				parentAcc,
				vault.emitEvent,
				"done",
				action.data
			);
			parentAcc = OpsParentSubChange.resetLoadedPrices(parentAcc, vault.api, {
				updateShipping: true,
			});
			return parentAcc;
		});
		if (_.get(action.payload, "contact.email")) {
			state = atUser(state).setIn(
				["model", "newEmail"],
				_.get(action, "data.newEmail")
			);
		}
		return state;
	},

	[AT.SAVE_PARENT_FORM_FAIL](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.setShippingSavingState(
				parentAcc,
				vault.emitEvent,
				"error",
				action.error
			);
			parentAcc = OpsParentAcc.saveFormFail(
				parentAcc,
				vault.emitEvent,
				action.error,
				action.moreInfo
			);
			return parentAcc;
		});
		return state;
	},

	[AT.DELETE_PARENT_CREDIT_CARD_REQUEST](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.deleteCreditCard(
				parentAcc,
				vault.emitEvent,
				vault.api,
				vault.clock.now()
			);
			return parentAcc;
		});
		return state;
	},

	[AT.DELETE_PARENT_CREDIT_CARD_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.deleteCreditCardSuccess(
				parentAcc,
				vault.emitEvent
			);
			return parentAcc;
		});
		return state;
	},

	[AT.DELETE_PARENT_CREDIT_CARD_FAIL](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.deleteCreditCardFail(
				parentAcc,
				vault.emitEvent,
				action.error,
				action.moreInfo
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SAVE_PARENT_STUDENT_INFO_REQUEST](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.editStudentInfo(
				parentAcc,
				vault.emitEvent,
				vault.api,
				action.studentID,
				action
			);
			return parentAcc;
		});
		if (action.grade || action.lockSettings) {
			// Clear parent data to be reloaded with correct grade/locks
			state = setIn(state, "officeStudentData", data => {
				return LoadState.loadRestart();
			});
			state = atUserLocks(state, action.studentID)
				.canCreate()
				.set(() => {
					return OpsLockOverrides.initUser(null, action.studentID);
				});
		}
		return state;
	},

	[AT.SAVE_PARENT_STUDENT_INFO_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.saveStudentInfoSuccess(
				parentAcc,
				vault.emitEvent
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SAVE_PARENT_STUDENT_INFO_FAIL](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.saveStudentInfoFail(
				parentAcc,
				vault.emitEvent,
				action.error,
				action.moreInfo
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SAVE_PARENT_NAME_REQUEST](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.editParentName(
				parentAcc,
				vault.emitEvent,
				vault.api,
				action
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SAVE_PARENT_NAME_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.saveParentNameSuccess(
				parentAcc,
				vault.emitEvent
			);
			return parentAcc;
		});
		if (action.payload.firstName !== undefined) {
			state = atUser(state).setIn(
				["model", "firstName"],
				action.payload.firstName
			);
		}
		if (action.payload.lastName !== undefined) {
			state = atUser(state).setIn(
				["model", "lastName"],
				action.payload.lastName
			);
		}
		return state;
	},

	[AT.SAVE_PARENT_NAME_FAIL](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.saveParentNameFail(
				parentAcc,
				vault.emitEvent,
				action.error,
				action.moreInfo
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SAVE_PARENT_PASSWORD_REQUEST](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.changeParentPassword(
				parentAcc,
				vault.emitEvent,
				vault.api,
				action.originalPassword,
				action.password,
				action.passwordRepeat
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SAVE_PARENT_PASSWORD_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.saveParentPasswordSuccess(
				parentAcc,
				vault.emitEvent
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SAVE_PARENT_PASSWORD_FAIL](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.saveParentPasswordFail(
				parentAcc,
				vault.emitEvent,
				action.error,
				action.moreInfo
			);
			return parentAcc;
		});
		return state;
	},

	[AT.PARENT_SAVE_STUDENT_PASSWORD_REQUEST](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.changeStudentPassword(
				parentAcc,
				vault.emitEvent,
				vault.api,
				action.studentID,
				action.password,
				action.passwordRepeat
			);
			return parentAcc;
		});
		return state;
	},

	[AT.PARENT_SAVE_STUDENT_PASSWORD_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.saveStudentPasswordSuccess(
				parentAcc,
				vault.emitEvent
			);
			return parentAcc;
		});
		return state;
	},

	[AT.PARENT_SAVE_STUDENT_PASSWORD_FAIL](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.saveStudentPasswordFail(
				parentAcc,
				vault.emitEvent,
				action.error,
				action.moreInfo
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SAVE_PARENT_SUBSCRIPTION_REQUEST](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.editSubscriptionInfo(
				parentAcc,
				vault.emitEvent,
				vault.api,
				action.studentID,
				action
			);
			return parentAcc;
		});
		if (action.hasOwnProperty("autoRenew")) {
			state = atParentPayments(state).set(ppay => {
				return OpsParentPayments.resetUpcoming(ppay);
			});
		}
		return state;
	},

	[AT.SAVE_PARENT_SUBSCRIPTION_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.saveSubscriptionInfoSuccess(
				parentAcc,
				vault.emitEvent
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SAVE_PARENT_SUBSCRIPTION_FAIL](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.saveSubscriptionInfoFail(
				parentAcc,
				vault.emitEvent,
				action.error,
				action.moreInfo
			);
			return parentAcc;
		});
		return state;
	},

	// Subscription change page flow

	[AT.SUBSCRIPTION_CHANGE_SET_USING_ACCOUNT_ADDRESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.setUsingAccountAddress(
				parentAcc,
				action.value
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_EDIT_SHIPPING](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.editShipping(parentAcc, action.values);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_SHOW_ALL_VALIDATION](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.validateShippingWithMissing(parentAcc);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_SET_DISPLAY_NAME](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.setDisplayName(
				parentAcc,
				vault.emitEvent,
				action.value
			);
			parentAcc = OpsParentSubChange.loadPricesIfNeeded(parentAcc, vault.api);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_SET_PLAN_TYPE](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.setPlanType(parentAcc, action.value);
			parentAcc = OpsParentSubChange.loadPricesIfNeeded(parentAcc, vault.api);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_SET_BUNDLE_GRADES](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.setBundleGrades(parentAcc, action.value);
			if ((action.value || []).length > 1) {
				parentAcc = OpsParentSubChange.setBookLevelDeferred(parentAcc, false);
			}
			parentAcc = OpsParentSubChange.loadPricesIfNeeded(parentAcc, vault.api);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_SET_BOOK_LEVEL_DEFERRED](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.setBookLevelDeferred(
				parentAcc,
				action.value
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_SET_AGREED_TO_BILLING](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.setAgreedToBilling(
				parentAcc,
				action.value
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_SET_SHIPPING_NEW_DEFAULT](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.setShippingIsNewDefault(
				parentAcc,
				action.value
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_VERIFY_SHIPPING_REQUEST](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.doVerifyShippingRequest(
				parentAcc,
				vault.api
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_VERIFY_SHIPPING_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.doVerifyShippingSuccess(
				parentAcc,
				vault.emitEvent,
				action.data
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_VERIFY_SHIPPING_FAIL](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.doVerifyShippingFail(
				parentAcc,
				vault.emitEvent,
				action.error
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_GET_PRICES_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.setLoadedPricesSuccess(
				parentAcc,
				action.generation,
				action.payload,
				action.data
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_GET_PRICES_FAIL](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.setLoadedPricesFail(
				parentAcc,
				action.generation,
				action.payload,
				action.error
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_GET_SWITCH_TYPE_DATA_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.setLoadedSwitchTypePricesSuccess(
				parentAcc,
				action.generation,
				action.payload,
				action.data
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_GET_SWITCH_TYPE_DATA_FAIL](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.setLoadedSwitchTypePricesFail(
				parentAcc,
				action.generation,
				action.payload,
				action.error
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_GET_PRICES_RETRY](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.loadPricesIfNeeded(parentAcc, vault.api);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_CLEAR_COUPON](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.clearCoupon(parentAcc, vault.api);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_GET_COUPON_REQUEST](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.getCouponRequest(
				parentAcc,
				action.couponCode,
				vault.api
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_GET_COUPON_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.getCouponSuccess(
				parentAcc,
				action.data,
				vault.emitEvent,
				vault.api
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBSCRIPTION_CHANGE_GET_COUPON_FAIL](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentSubChange.getCouponFail(
				parentAcc,
				action.error,
				vault.emitEvent
			);
			return parentAcc;
		});
		return state;
	},

	[AT.FINISH_SUBSCRIPTION_CHANGE_REQUEST](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			if (OpsParentSubChange.getPricesLoadStatus(parentAcc) === "done") {
				parentAcc = OpsParentSubChange.doFinishRequest(parentAcc, vault.api);
			} else {
				vault.emitEvent("showOfficeModal", "parentSCSPricesNotDone");
			}
			return parentAcc;
		});
		return state;
	},

	[AT.FINISH_SUBSCRIPTION_CHANGE_SUCCESS](vault, state, action) {
		let result;
		state = atParentAccount(state).set(parentAcc => {
			result = OpsParentSubChange.doFinishSuccess(parentAcc, action.data); //action.data = response from backend API call
			return result.parentAcc;
		});
		if (result.step) {
			state = atPage(state).setIn("step", result.step);
		}
		if (result.parentPermUpdates) {
			state = atUser(state).setIn(["model", "perms"], p =>
				_.extend({}, p, result.parentPermUpdates)
			);
		}

		state = setIn(state, "officeStudentData", LoadState.loadRestart);
		return state;
	},

	[AT.FINISH_SUBSCRIPTION_CHANGE_FAIL](vault, state, action) {
		let result;
		state = atParentAccount(state).set(parentAcc => {
			result = OpsParentSubChange.doFinishError(
				parentAcc,
				vault.emitEvent,
				vault.api,
				action.error,
				action.moreInfo
			);
			return result.parentAcc;
		});
		if (result.shouldLeave) {
			state = atPage(state).set(URLMake.state.forAccount());
		} else if (result.step) {
			state = atPage(state).setIn("step", result.step);
		}
		return state;
	},

	[AT.FINISH_SUBSCRIPTION_CANCEL_REQUEST](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.sendCancelRequest(
				parentAcc,
				vault.api,
				action.studentID,
				action.refundBooks,
				action.refundOnline
			);
			return parentAcc;
		});
		return state;
	},

	[AT.FINISH_SUBSCRIPTION_CANCEL_SUCCESS](vault, state, action) {
		const refundOnline = _.get(action, "payload.refundOnline");
		const refundBooks = _.get(action, "payload.refundBooks");
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.onCancelSuccess(
				parentAcc,
				vault.emitEvent,
				action.studentID,
				refundBooks,
				refundOnline
			);
			return parentAcc;
		});
		state = setIn(state, "officeStudentData", LoadState.loadRestart);
		// Update active status for students in user model
		const studentCR = atUserStudents(state, action.studentID);
		// refundBooks and refundOnline are booleans if a bundle was
		// cancelled. Otherwise they are not defined.
		const onlyCancelledBooks = action.refundBooks && !action.refundOnline;
		if (studentCR.exists() && !onlyCancelledBooks) {
			state = studentCR.setIn("isActive", false);
		}
		return state;
	},

	[AT.FINISH_SUBSCRIPTION_CANCEL_FAIL](vault, state, action) {
		let result;
		state = atParentAccount(state).set(parentAcc => {
			result = OpsParentAcc.onCancelFail(
				parentAcc,
				vault.emitEvent,
				action.error,
				action.studentID
			);
			return result.parentAcc;
		});
		// Not handling result.shouldLeave ATM, since cancel done on same page
		return state;
	},

	[AT.SUBMIT_CANCEL_REASON_REQUEST](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.sendCancelReasonRequest(
				parentAcc,
				vault.api,
				action.subscriptionID,
				action.reason
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBMIT_CANCEL_REASON_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.onCancelReasonSuccess(
				parentAcc,
				vault.emitEvent
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SUBMIT_CANCEL_REASON_FAIL](vault, state, action) {
		vault.emitEvent("cancelReasonFail");
		return state;
	},

	// Payments

	[AT.LOAD_PARENT_UPCOMING_PAYMENTS_REQUEST](vault, state, action) {
		state = atParentPayments(state).set(ppay => {
			return OpsParentPayments.loadUpcomingStart(ppay, vault.api);
		});
		return state;
	},

	[AT.LOAD_PARENT_UPCOMING_PAYMENTS_SUCCESS](vault, state, action) {
		state = atParentPayments(state).set(ppay => {
			return OpsParentPayments.loadUpcomingSuccess(ppay, action.data);
		});
		return state;
	},

	[AT.LOAD_PARENT_UPCOMING_PAYMENTS_FAIL](vault, state, action) {
		state = atParentPayments(state).set(ppay => {
			return OpsParentPayments.loadUpcomingFail(ppay, action.error);
		});
		return state;
	},

	[AT.LOAD_PARENT_MORE_PAST_PAYMENTS_REQUEST](vault, state, action) {
		state = atParentPayments(state).set(ppay => {
			return OpsParentPayments.loadMorePastStart(ppay, vault.api);
		});
		return state;
	},

	[AT.LOAD_PARENT_MORE_PAST_PAYMENTS_SUCCESS](vault, state, action) {
		const {limit, offset} = action.payload;
		state = atParentPayments(state).set(ppay => {
			return OpsParentPayments.loadMorePastSuccess(
				ppay,
				limit,
				offset,
				action.data
			);
		});
		return state;
	},

	[AT.LOAD_PARENT_MORE_PAST_PAYMENTS_FAIL](vault, state, action) {
		state = atParentPayments(state).set(ppay => {
			return OpsParentPayments.loadMorePastFail(ppay, action.error);
		});
		return state;
	},

	[AT.EMAIL_RECEIPT_REQUEST](vault, state, action) {
		const {orderID} = action;
		vault.api.emailReceipt(orderID);
		return state;
	},
	[AT.EMAIL_RECEIPT_SUCCESS](vault, state, action) {
		const {orderID} = action.payload;
		vault.emitEvent("emailReceiptSuccess", orderID);
		return state;
	},
	[AT.EMAIL_RECEIPT_FAIL](vault, state, action) {
		// no-op
		return state;
	},

	[AT.GET_RANDOM_USERNAMES_FOR_PARENT_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showMessage", "disconnected");
			return state;
		}
		const {studentID} = action;
		const user = atOfficeStudentData(state, studentID).get();
		if (user.isLoadingUsernames) {
			//Already in the process of loading. Keep waiting
			return;
		}
		state = atOfficeStudentData(state, studentID).setIn(
			"isLoadingUsernames",
			true
		);
		vault.api.generateParentRandomUsernames(studentID, user.registrationCode);
		track(STUDENT_REGISTRATION_EVENTS.USERNAME_REROLL, {
			studentId: studentID,
		});
		return state;
	},

	[AT.GET_RANDOM_USERNAMES_FOR_PARENT_SUCCESS](vault, state, action) {
		const {userID} = action.payload;
		state = atOfficeStudentData(state, userID).extend({
			isLoadingUsernames: false,
			possibleUsernames: action.data.usernames,
		});
		return state;
	},

	[AT.GET_RANDOM_USERNAMES_FOR_PARENT_FAIL](vault, state, action) {
		const {userID} = action.payload;
		state = atOfficeStudentData(state, userID).setIn(
			"isLoadingUsernames",
			false
		);
		vault.emitEvent("parentUsernamesGenerationIssue", {
			errorType: action.error,
			studentID: userID,
		});
		return state;
	},

	[AT.STUDENT_PRIMARY_SETUP_GENERATE_USERNAME_REQUEST](vault, state, action) {
		const {studentID, usernameAdjective, usernameAnimal} = action.payload;
		const student = atOfficeStudentData(state, studentID).get();
		state = atOfficeStudentData(state, studentID).setIn(
			"isLoadingUsername",
			true
		);
		vault.api.generateUsernameNumberSegment(
			studentID,
			student.registrationCode,
			{
				adjective: usernameAdjective,
				noun: usernameAnimal,
			}
		);
		return state;
	},
	[AT.STUDENT_PRIMARY_SETUP_GENERATE_USERNAME_SUCCESS](vault, state, action) {
		const {
			data,
			payload: {studentID},
		} = action;

		state = atOfficeStudentData(state, studentID).extend({
			isLoadingUsername: false,
			possibleUsername: data.username,
		});
		// Route through goToPrimarySetupStep so the per-device last-step
		// localStorage entry stays in sync — direct mutation here would skip it.
		state = goToPrimarySetupStep("USERNAME_CONFIRMATION", studentID, state);
		return state;
	},
	[AT.STUDENT_PRIMARY_SETUP_GENERATE_USERNAME_FAIL](vault, state, action) {
		const {userID} = action.payload;
		state = atOfficeStudentData(state, userID).setIn(
			"isLoadingUsername",
			false
		);
		return state;
	},
	[AT.GET_UNAVAILABLE_USERNAME_COMBINATIONS_REQUEST](vault, state, action) {
		vault.api.getUnavailableUsernameCombinations();
		return state;
	},
	[AT.GET_UNAVAILABLE_USERNAME_COMBINATIONS_SUCCESS](vault, state, action) {
		state = atPage(state).setIn(
			"unavailableUsernameCombinations",
			action.data.unavailableUsernameCombinations
		);
		return state;
	},
	[AT.GET_UNAVAILABLE_USERNAME_COMBINATIONS_FAIL](vault, state, action) {
		return state;
	},

	[AT.PARENT_UPDATE_UNREGISTERED_USER_REQUEST](vault, state, action) {
		//TODO: Dump loaded account data when performing this action (or maybe on success only)
		const {studentID} = action;
		//If just started, move to first step
		if (action.start) {
			state = atOfficeStudentData(state, studentID).setIn(
				"registrationStep",
				"customerSegment"
			);
			track(STUDENT_REGISTRATION_EVENTS.START, {
				studentId: studentID,
			});
			return state;
		}
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal", "studentRegistration");
			return state;
		}
		const student = atOfficeStudentData(state, studentID).get();
		if (student.isSavingRegistration) {
			//Save already in progress, do nothing.
			return;
		}
		//Mark that an update is in progress
		state = atOfficeStudentData(state, studentID).setIn(
			"isSavingRegistration",
			true
		);

		vault.api.parentSecondaryRegistrationUpdateStudent(
			studentID,
			student.platformUserID,
			action,
			action.step
		);

		return state;
	},

	[AT.PARENT_UPDATE_UNREGISTERED_USER_SUCCESS](vault, state, action) {
		const {user} = action.payload;
		const {userID} = user;

		// Force all account data to reload next time it's visited.
		state = atParentAccount(state).set(pa => OpsParentAcc.markForReload(pa));

		//Unmark that an update is in progress
		state = atOfficeStudentData(state, userID).setIn(
			"isSavingRegistration",
			false
		);

		const student = atOfficeStudentData(state, userID).get();

		// If the user is going back, we need to move to the previous step on the frontend
		if (user.step === "back") {
			const currentStep = student.registrationStep;
			const stepMap = {
				customerSegment: "welcome",
				baLevel: "customerSegment",
				lockScheme: "customerSegment",
				username: "lockScheme",
			};
			const previousStep = stepMap[currentStep];
			if (previousStep) {
				state = atOfficeStudentData(state, userID).setIn(
					"registrationStep",
					previousStep
				);
				track(STUDENT_REGISTRATION_EVENTS.BACK, {
					studentId: userID,
					stepCurrentlyOn: currentStep,
					stepGoingBackTo: previousStep,
				});
			}
		} else if (_.get(user, "settings.customerSegment")) {
			// Fill in any changed info for the studentData and
			// move to the next step.
			state = atOfficeStudentData(state, userID).set(s =>
				_.extend({}, s, {
					registrationStep: "lockScheme",
				})
			);
		} else if (_.get(user, "settings.grade")) {
			state = atOfficeStudentData(state, userID).set(s =>
				_.extend({}, s, {
					grade: user.settings.grade,
					registrationStep: "lockScheme",
				})
			);
		} else if (_.get(user, "settings.lockSettings")) {
			state = atOfficeStudentData(state, userID).set(s =>
				_.extend({}, s, {
					lockSettings: user.settings.lockSettings,
					hasRegistered: true,
					hasCompletedRegistration: true,
					registrationStep: "final",
				})
			);
			// Mark that the full officeStudentData will need a reload to show locks correctly
			// Use a flag so the reload can wait until after registration when accessing
			// the student's page.
			state = setIn(state, ["officeStudentData", "needsDataReload"], true);
		} else if (user.username) {
			if (student.registrationStatus.hasSetPassword) {
				// Academy users already have a password set, so skip this step in that case.
				state = atOfficeStudentData(state, userID).set(s =>
					_.extend({}, s, {
						username: user.username,
						hasRegistered: false,
						hasCompletedRegistration: false,
						registrationStep: "welcome",
					})
				);
				state = updateUserStudentsList(state, userID, {
					username: user.username,
					hasRegistered: false,
					hasCompletedRegistration: false,
				});
			} else {
				state = atOfficeStudentData(state, userID).set(s =>
					_.extend({}, s, {
						username: user.username,
						registrationStep: "password",
					})
				);
				state = updateUserStudentsList(state, userID, {
					username: user.username,
				});
			}
		} else if (user.password) {
			if (
				!_.some(_.values(atParentStudents(state).get()), s => s.hasRegistered)
			) {
				vault.emitEvent("showParentTutorial", {step: "supportMenu"});
			}
			state = atOfficeStudentData(state, userID).set(s =>
				_.extend({}, s, {
					hasRegistered: false,
					registrationStep: "welcome",
				})
			);
			state = updateUserStudentsList(state, userID, {hasRegistered: false});
		}
		return state;
	},

	[AT.PARENT_UPDATE_UNREGISTERED_USER_FAIL](vault, state, action) {
		const {userID} = action.payload.user;

		//Unmark that an update is in progress
		state = atOfficeStudentData(state, userID).setIn(
			"isSavingRegistration",
			false
		);
		//And emit that an error occurred
		if (
			_.includes(
				["E_REGISTRATION_IN_PROGRESS", "E_CODE_USED", "E_CODE_EXPIRED"],
				action.error
			)
		) {
			//emit a different sort of error to throw an error modal for these since a refresh is necessary
			vault.emitEvent("officeParentError", "studentRegistrationInProgress", {
				needsReload: true,
				isWarning: true,
			});
		} else {
			vault.emitEvent("parentUpdateUnregisteredUserError", {
				errorType: action.error,
				studentID: userID,
			});
		}
		return state;
	},

	[AT.STUDENT_PRIMARY_SETUP_ADVANCE_STEP_REQUEST](vault, state, action) {
		const {studentID, step} = action.payload;
		const nextStep = action.payload.nextStep ?? getNextPrimarySetupStep(step);
		if (!nextStep) {
			// We're on the final step, this normally shouldn't be called
			return state;
		}
		const student = atOfficeStudentData(state, studentID).get();

		// Some steps don't require a server call, so we can just go to the next step.
		// (AGE has no payload, so it submits a no-op request and then advances client-side.)
		const OFFLINE_STEPS = ["START", "AGE"];
		const shouldSkipUsernameSubmit =
			student.username && step === "USERNAME_SELECTION";
		const isOfflineStep =
			OFFLINE_STEPS.includes(step) || shouldSkipUsernameSubmit;
		if (isOfflineStep) {
			state = goToPrimarySetupStep(nextStep, studentID, state);
			return state;
		}

		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal", "studentRegistration");
			return state;
		}
		if (student.isSavingRegistration) {
			//Save already in progress, do nothing.
			return;
		}
		const userPayload = _.omit(action.payload, [
			"type",
			"studentID",
			"nextStep",
		]);
		const payloadForApi =
			step === "DISPLAY_NAME"
				? _.extend({}, userPayload, {
						displayName: userPayload.displayName ?? student.displayName,
				  })
				: userPayload;

		//Mark that an update is in progress
		state = atOfficeStudentData(state, studentID).setIn(
			"isSavingRegistration",
			true
		);
		//Sending in the updates that they've made
		vault.api.parentUpdatePrimarySetupUser(
			studentID,
			student.registrationCode,
			payloadForApi,
			step,
			nextStep
		);

		return state;
	},

	[AT.STUDENT_PRIMARY_SETUP_ADVANCE_STEP_SUCCESS](vault, state, action) {
		const {user, step} = action.payload;
		const {userID} = user;
		const nextStep = action.payload.nextStep ?? getNextPrimarySetupStep(step);

		// Force account data reload only when the server flow is finished.
		if (nextStep === "FINAL" || nextStep == null) {
			state = atParentAccount(state).set(pa => OpsParentAcc.markForReload(pa));
		}

		//Unmark that an update is in progress
		state = atOfficeStudentData(state, userID).setIn(
			"isSavingRegistration",
			false
		);

		state = goToPrimarySetupStep(nextStep, userID, state);
		if (step === "DISPLAY_NAME" && user.displayName) {
			state = atOfficeStudentData(state, userID).set(s => ({
				...s,
				displayName: user.displayName,
			}));
			state = updateUserStudentsList(state, userID, {
				displayName: user.displayName,
			});
		} else if (user.settings?.grade) {
			state = atOfficeStudentData(state, userID).set(s => {
				const eventsTriggered = _.cloneDeep(s.eventsTriggered || {});
				_.unset(eventsTriggered, ["tutorials", "studentOnboardingV2"]);
				return {
					...s,
					grade: user.settings.grade,
					eventsTriggered,
					registrationStatus: {
						...s.registrationStatus,
						hasSetGrade: true,
					},
				};
			});
			if (nextStep === "FINAL") {
				state = atOfficeStudentData(state, userID).set(s => ({
					...s,
					hasRegistered: true,
					hasCompletedRegistration: true,
					registrationStep: "final",
				}));
				state = updateUserStudentsList(state, userID, {
					hasRegistered: true,
					hasCompletedRegistration: true,
				});
			}
		} else if (user.settings?.age != null) {
			state = atOfficeStudentData(state, userID).set(s => ({
				...s,
				age: user.settings.age,
			}));
		} else if (user.username) {
			if (user.hasAcademyPurchase) {
				// Academy users already have a password set, so skip this step in that case.
				state = atOfficeStudentData(state, userID).set(s => ({
					...s,
					username: user.username,
					hasRegistered: true,
					hasCompletedRegistration: true,
					registrationStep: "final",
				}));
				state = updateUserStudentsList(state, userID, {
					username: user.username,
					hasRegistered: true,
					hasCompletedRegistration: true,
				});
				state = goToPrimarySetupStep("FINAL", userID, state);
			} else {
				state = atOfficeStudentData(state, userID).set(s => ({
					...s,
					username: user.username,
					registrationStep: "password",
				}));
				state = updateUserStudentsList(state, userID, {
					username: user.username,
				});
			}
		} else if (user.password) {
			if (nextStep === "FINAL") {
				// V1/V2: password is the last server step before FINAL.
				state = atOfficeStudentData(state, userID).set(s => ({
					...s,
					hasRegistered: true,
					hasCompletedRegistration: true,
					registrationStep: "final",
				}));
				state = updateUserStudentsList(state, userID, {
					hasRegistered: true,
					hasCompletedRegistration: true,
				});
			} else {
				// V3: password saved; BA_LEVEL (or placement branch) still remains.
				// Keep the student eligible for the setup flow — parent-data reloads
				// derive hasCompletedRegistration from codes.used on the server.
				state = atOfficeStudentData(state, userID).set(s => ({
					...s,
					hasRegistered: false,
					hasCompletedRegistration: false,
				}));
			}
		}
		if (user.settings?.shouldTakePlacementTest === true) {
			state = atOfficeStudentData(state, userID).set(s => ({
				...s,
				eventsTriggered: _.set(
					_.cloneDeep(s.eventsTriggered || {}),
					["tutorials", "studentOnboardingV2"],
					{
						currentPart: "beginStudentOnboardingV2",
						currentStep: "prePlacementTest",
					}
				),
				registrationStatus: {
					...s.registrationStatus,
					hasSetShouldTakePlacementTest: true,
				},
			}));
		} else if (user.settings?.shouldTakePlacementTest === false) {
			state = atOfficeStudentData(state, userID).set(s => {
				const eventsTriggered = _.cloneDeep(s.eventsTriggered || {});
				_.unset(eventsTriggered, ["tutorials", "studentOnboardingV2"]);
				return {
					...s,
					eventsTriggered,
					registrationStatus: {
						...s.registrationStatus,
						hasSetShouldTakePlacementTest: true,
					},
				};
			});
		}
		return state;
	},
	[AT.STUDENT_PRIMARY_SETUP_ADVANCE_STEP_FAIL](vault, state, action) {
		const {userID} = action.payload.user;

		//Unmark that an update is in progress
		state = atOfficeStudentData(state, userID).setIn(
			"isSavingRegistration",
			false
		);
		//And emit that an error occurred
		if (
			_.includes(
				["E_REGISTRATION_IN_PROGRESS", "E_CODE_USED", "E_CODE_EXPIRED"],
				action.error
			)
		) {
			//emit a different sort of error to throw an error modal for these since a refresh is necessary
			vault.emitEvent("officeParentError", "studentRegistrationInProgress", {
				needsReload: true,
				isWarning: true,
			});
		} else {
			vault.emitEvent("parentUpdateUnregisteredUserError", {
				errorType: action.error,
				studentID: userID,
			});
		}
		return state;
	},

	[AT.STUDENT_PRIMARY_SETUP_GO_TO_STEP](vault, state, action) {
		const {studentID, step} = action.payload;
		state = goToPrimarySetupStep(step, studentID, state);
		return state;
	},

	[AT.PARENT_OFFICE_STUDENT_DATA_RELOAD](vault, state, action) {
		if (getIn(state, ["officeStudentData", "needsDataReload"])) {
			state = setIn(state, "officeStudentData", data => {
				return LoadState.loadRestart();
			});
		}
		return state;
	},

	// Beast Builder can only be force locked and not force unlocked.
	[AT.SET_STUDENT_BEASTBUILDER_LOCK_OVERRIDE_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal");
			return state;
		}
		const {value, studentID} = action;
		const lockStatus = value ? null : "locked";
		state = atParentAccount(state).set(parentAcc => {
			return OpsParentAcc.editBeastbuilderAccess(
				parentAcc,
				vault.emitEvent,
				vault.api,
				studentID,
				lockStatus
			);
		});
		return state;
	},

	[AT.SET_STUDENT_BEASTBUILDER_LOCK_OVERRIDE_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.saveSubscriptionInfoSuccess(
				parentAcc,
				vault.emitEvent
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SET_STUDENT_BEASTBUILDER_LOCK_OVERRIDE_FAIL](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.saveSubscriptionInfoFail(
				parentAcc,
				vault.emitEvent,
				action.error,
				action.moreInfo
			);
			return parentAcc;
		});
		return state;
	},

	// Endpoints for locking / unlocking leaderboards
	[AT.SET_STUDENT_LEADERBOARDS_LOCK_OVERRIDE_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal");
			return state;
		}
		const {value, studentID} = action;
		const lockStatus = value ? null : "locked";
		state = atParentAccount(state).set(parentAcc => {
			return OpsParentAcc.editLeaderboardsAccess(
				parentAcc,
				vault.emitEvent,
				vault.api,
				studentID,
				lockStatus
			);
		});
		return state;
	},

	[AT.SET_STUDENT_LEADERBOARDS_LOCK_OVERRIDE_SUCCESS](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.saveSubscriptionInfoSuccess(
				parentAcc,
				vault.emitEvent
			);
			return parentAcc;
		});
		return state;
	},

	[AT.SET_STUDENT_LEADERBOARDS_LOCK_OVERRIDE_FAIL](vault, state, action) {
		state = atParentAccount(state).set(parentAcc => {
			parentAcc = OpsParentAcc.saveSubscriptionInfoFail(
				parentAcc,
				vault.emitEvent,
				action.error,
				action.moreInfo
			);
			return parentAcc;
		});
		return state;
	},

	// Bulk code redemption updates the parent
	[AT.REDEEM_SUBSCRIPTION_CODES_REQUEST](vault, state, action) {
		const {parent, students, codes} = action;
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal");
			return state;
		}
		vault.api.redeemSubscriptionCodes(parent, students, codes);
	},

	[AT.REDEEM_SUBSCRIPTION_CODES_SUCCESS](vault, state, action) {
		const response = action.data;
		if (response.error) {
			vault.emitEvent("showOfficeModal", "redeemCodesFail");
		} else {
			vault.emitEvent("redeemSubscriptionCodesSuccess");
			const user = atUser(state).get();
			// Update parent data so the new subscriptions
			// appear in the Account and Reports pages.
			if (user && OpsUser.isLoggedIn(user)) {
				if (OpsUser.isPlatformGuest(user)) {
					state = atUser(state).set(u => {
						return OpsUser.loadRestart(u, vault.api);
					});
				}
				state = atParentAccount(state).set(parentAcc => {
					parentAcc = OpsParentAcc.markForReload(parentAcc);
					return parentAcc;
				});

				//Set up perm changes for parent
				const parentPermUpdates = {};
				_.each(response.studentIDs, studentID => {
					parentPermUpdates[`student view#${studentID}`] = true;
					parentPermUpdates[`student lock#${studentID}`] = true;
				});

				state = atUser(state).setIn(["model", "perms"], p =>
					_.extend({}, p, parentPermUpdates)
				);

				state = setIn(state, "officeStudentData", LoadState.loadRestart);
			}
		}
		return state;
	},

	[AT.REDEEM_SUBSCRIPTION_CODES_FAIL](vault, state, action) {
		vault.emitEvent("showOfficeModal", "redeemCodesFail");
	},
};

//
// Post-update hooks
//

const syncSubChangeToPage = function(vault, oldState, newState, action) {
	const oldPage = atPage(oldState).get();
	const page = atPage(newState).get();
	const oldParent = atParentAccount(oldState).get();
	const parent = atParentAccount(newState).get();
	// Things that can trigger this sync.
	const justLoadedBundle = action.type === AT.OFFICE_INITIAL;
	const justChangedPages = page && oldPage !== page;
	const justLoadedParent =
		_.get(parent, "load") === "done" && _.get(oldParent, "load") !== "done";
	if (!justLoadedBundle && !justChangedPages && !justLoadedParent)
		return newState;
	const parentData = Slots.getValue(newState, Slots.table.parentData());
	newState = atParentAccount(newState).set(p =>
		OpsParentSubChange.syncToPage(p, page, vault.api, parentData)
	);
	const redirect = atParentAccount(newState).getIn("subChangeState.redirect");
	if (redirect) {
		newState = atPage(newState).set(URLMake.state.forAccount());
		if (page.changeType === "add") {
			vault.emitEvent("attemptToAddStudent");
		}
	}
	newState = fixSubChangePageStep(newState);
	return newState;
};

//
// Exported functions to augment vault update
//

const augment = function(updater) {
	updater.addActionHandler(actionHandlers);
	updater.addActionHandler(UpdateReport.actionHandlers);

	updater.addPostUpdateHook(syncSubChangeToPage, "parent:syncSubChangeToPage");
};

module.exports = {
	augment,
};
